/**
 * Routes the grid keyboard shortcuts to exactly one grid:
 *   Ctrl+Shift+F (Cmd+Shift+F)  "Jump to column" picker
 *   Ctrl+F (Cmd+F)              "Find in grid"
 * Some pages mount several grids at once (the Pricetool "All" tab shows
 * Listings and Inventory, and detail panels can nest more), so instead of
 * every grid listening on window and every picker opening, each grid's
 * toolbar registers its handlers here and a single shared listener opens the
 * one belonging to:
 *   1. the grid the user last focused or pressed a pointer inside, else
 *   2. the first registered top-level grid that is on screen.
 * Interaction is tracked per grid root, so a grid's JumpToColumn and
 * FindInGrid (registered separately) share it. The window/document listeners
 * exist only while at least one grid is mounted.
 */

export type GridShortcut = "jumpToColumn" | "find";

export interface JumpToColumnTarget {
  /** Element that counts as "inside this grid" for interaction tracking. */
  getRoot: () => HTMLElement | null;
  /** Opens this grid's picker (or find box). */
  open: () => void;
}

interface Registration extends JumpToColumnTarget {
  shortcut: GridShortcut;
}

// In registration (mount) order
const targets: Registration[] = [];
let lastInteractedRoot: HTMLElement | null = null;

type ShortcutKeys = Pick<
  KeyboardEvent,
  "key" | "ctrlKey" | "metaKey" | "shiftKey" | "altKey"
>;

export function isJumpToColumnShortcut(e: ShortcutKeys): boolean {
  return (
    (e.ctrlKey || e.metaKey) &&
    e.shiftKey &&
    !e.altKey &&
    e.key.toLowerCase() === "f"
  );
}

export function isFindInGridShortcut(e: ShortcutKeys): boolean {
  return (
    (e.ctrlKey || e.metaKey) &&
    !e.shiftKey &&
    !e.altKey &&
    e.key.toLowerCase() === "f"
  );
}

// Unmounted or display:none grids (e.g. an inactive tab) can't show a picker
const isOnScreen = (root: HTMLElement | null): root is HTMLElement =>
  !!root && root.getClientRects().length > 0;

/**
 * The innermost registered grid root that contains `node`, so a click inside
 * a nested sub-grid is credited to that grid rather than to its parent.
 */
function findRootContaining(node: Node | null): HTMLElement | null {
  if (!node) return null;
  let best: HTMLElement | null = null;
  for (const target of targets) {
    const root = target.getRoot();
    if (!root || !root.contains(node)) continue;
    if (!best || best.contains(root)) best = root;
  }
  return best;
}

const isTopLevel = (root: HTMLElement) =>
  !targets.some((other) => {
    const otherRoot = other.getRoot();
    return !!otherRoot && otherRoot !== root && otherRoot.contains(root);
  });

/** The grid the shortcut should open its picker/find box for, if any. */
export function resolveShortcutTarget(
  shortcut: GridShortcut
): JumpToColumnTarget | null {
  const candidates = targets.filter((t) => t.shortcut === shortcut);
  if (isOnScreen(lastInteractedRoot)) {
    const interacted = candidates.find(
      (t) => t.getRoot() === lastInteractedRoot
    );
    if (interacted) return interacted;
  }
  return (
    candidates.find((t) => {
      const root = t.getRoot();
      return isOnScreen(root) && isTopLevel(root);
    }) ?? null
  );
}

export function resolveJumpToColumnTarget(): JumpToColumnTarget | null {
  return resolveShortcutTarget("jumpToColumn");
}

const handleInteraction = (e: Event) => {
  const root = findRootContaining(e.target as Node | null);
  if (root) lastInteractedRoot = root;
};

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.defaultPrevented) return;
  const shortcut: GridShortcut | null = isJumpToColumnShortcut(e)
    ? "jumpToColumn"
    : isFindInGridShortcut(e)
    ? "find"
    : null;
  if (!shortcut) return;
  // Leave the keyboard to an open dialog, menu or popover (including an
  // already-open picker) instead of opening a picker behind it
  if (document.activeElement?.closest('.MuiModal-root, [role="dialog"]')) {
    return;
  }

  // With no target (no grid on screen) the browser keeps its own Ctrl+F
  const target = resolveShortcutTarget(shortcut);
  if (!target) return;
  e.preventDefault();
  target.open();
};

function register(
  shortcut: GridShortcut,
  target: JumpToColumnTarget
): () => void {
  const registration: Registration = { ...target, shortcut };
  targets.push(registration);
  if (targets.length === 1) {
    window.addEventListener("keydown", handleKeyDown);
    // Capture phase so grids that stop propagation still count as interacted
    document.addEventListener("pointerdown", handleInteraction, true);
    document.addEventListener("focusin", handleInteraction, true);
  }

  return () => {
    const index = targets.indexOf(registration);
    if (index === -1) return;
    targets.splice(index, 1);
    if (!targets.some((t) => t.getRoot() === lastInteractedRoot)) {
      lastInteractedRoot = null;
    }
    if (targets.length === 0) {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handleInteraction, true);
      document.removeEventListener("focusin", handleInteraction, true);
    }
  };
}

/**
 * Registers a grid's column picker. Returns the cleanup to call on unmount,
 * which also tears the shared listeners down once the last grid is gone.
 */
export function registerJumpToColumnTarget(
  target: JumpToColumnTarget
): () => void {
  return register("jumpToColumn", target);
}

/** Registers a grid's find box (Ctrl+F). Same contract as above. */
export function registerFindInGridTarget(
  target: JumpToColumnTarget
): () => void {
  return register("find", target);
}
